import { useForm } from 'react-hook-form';
import { usePreservedCallback } from '@toss/react';
import { GlobeFillIcon } from '@zep-us/zus';
import { Blue500 } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { useWebsiteErrorAlert, validateWebsiteUrl, type WebsiteData } from '@/api/ai/website';
import { useEffectOnce } from '@/hooks';
import { XIcon } from '@/icons';
import { LoadingSpinner } from '@/shared/components';
import { alertDialog, Button, IconButton, TextField } from '@/ui_v3';
import { cn } from '@/utils';
export const WebsiteUrlTextField = ({
  websiteData,
  onSearch,
  onCancel,
  isLoading = false
}: {
  websiteData: WebsiteData | null;
  // useWebsitePreview는 실패/isExtractable=false 시 null을 반환한다 —
  // 호출부에서 반환값으로 success path를 분기한다.
  onSearch: (url: string) => Promise<unknown>;
  onCancel: () => void;
  isLoading?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    getValues,
    reset,
    watch,
    register,
    formState,
    setFocus
  } = useForm({
    defaultValues: {
      url: websiteData?.url || ''
    },
    mode: 'onSubmit'
  });

  // 검증 실패 알럿은 preview 단계와 동일한 AlertGroup 매핑·메시지·로그를
  // 공유한다 — INVALID_URL/UNUSABLE_URL/FILE_URL이 동일하게 흘러간다.
  const alert = useWebsiteErrorAlert('preview');

  /**
   * URL 검증 및 웹사이트 정보 조회 함수
   */
  const handleConfirmUrl = async () => {
    const url = getValues('url');
    if (!url) {
      alertDialog.open({
        header: {
          title: t('build:build.website.urlField.emptyAlert')
        }
      });
      return;
    }
    const result = validateWebsiteUrl(url);
    if (!result.isValid) {
      alert.fromValidation(result);
      return;
    }

    // 검증 후 정규화된 URL을 form value와 onSearch 양쪽에 동일하게 흘려 보낸다.
    // 그렇지 않으면 사용자가 다시 검색할 때 form은 raw input, 서버는
    // normalized URL을 봐 mismatch가 발생한다.
    //
    // preview 실패(useWebsitePreview가 null 반환) 시에는 reset을 건너뛰어
    // form을 dirty 상태로 유지한다 — pristine 상태가 되면 confirm 버튼이
    // 숨겨져 같은 URL로 즉시 재시도하기 어려워진다.
    const data = await onSearch(result.normalizedUrl);
    if (data) {
      reset({
        url: result.normalizedUrl
      });
    }
  };
  const handleCancel = usePreservedCallback(() => {
    reset({
      url: ''
    });
    onCancel();
  });
  useEffectOnce(() => {
    // 모달이 열리고 100ms 후에 텍스트 필드에 포커스
    setTimeout(() => {
      setFocus('url');
    }, 100);
  });
  return <TextField {...register('url')} required intent="strong" placeholder={t('build:build.website.urlField.placeholder')} onKeyDown={e => {
    if (e.key === 'Enter' && !isLoading) {
      void handleConfirmUrl();
    }
  }} autoFocus leftSlot={<GlobeFillIcon size="md" className="shrink-0" fill={Blue500} />} rightSlot={<div className="flex flex-row items-center gap-4xs">
          <IconButton onClick={handleCancel} className={cn(!websiteData && 'hidden')} size="md" variant={'transparent'} intent={'neutral'}>
            <XIcon size="md" />
          </IconButton>
          <Button size="sm" variant="outlined" intent="neutral" className={cn(!formState.isDirty && !!watch('url') && 'hidden')} disabled={!watch('url') || isLoading} onClick={() => void handleConfirmUrl()} rightSlot={isLoading ? <LoadingSpinner className="text-gray-400" /> : undefined}>
            {t('common:global.confirm.button')}
          </Button>
        </div>} data-sentry-element="TextField" data-sentry-component="WebsiteUrlTextField" data-sentry-source-file="WebsiteUrlTextField.tsx" />;
};